import React from 'react';
import { X, Cpu, Globe, Brain, Zap } from 'lucide-react';
import { AppSettings, ModelType, AppLanguage, TRANSLATIONS } from '../types';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  updateSettings: (newSettings: Partial<AppSettings>) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, settings, updateSettings }) => {
  if (!isOpen) return null;

  const t = TRANSLATIONS[settings.language];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md bg-nular-panel border border-nular-border shadow-2xl overflow-y-auto max-h-[90vh] animate-slide-up-fade rounded-none">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-nular-border bg-nular-panel sticky top-0 z-10">
          <h2 className="text-xl font-mono font-bold text-nular-silver uppercase tracking-tight">{t.settings}</h2>
          <button onClick={onClose} className="p-2 text-nular-dim hover:text-nular-accent transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          
          {/* Models */}
          <div className="space-y-4">
            <label className="text-xs uppercase tracking-wider text-nular-dim font-bold flex items-center gap-2">
              <Cpu size={14} /> {t.models}
            </label>
            <div className="grid gap-2">
              <button
                onClick={() => updateSettings({ model: ModelType.NULAR_FLASH })}
                className={`p-4 text-left border transition-all rounded-none ${
                  settings.model === ModelType.NULAR_FLASH
                    ? 'bg-nular-border/50 border-nular-silver text-nular-accent' 
                    : 'bg-transparent border-nular-border text-nular-dim hover:border-nular-dim'
                }`}
              >
                <div className="font-mono font-bold text-sm">Nular Flash (Mistral 7B)</div>
                <div className="text-xs opacity-60 mt-1">Balanced speed and capability. Hosted on HF.</div>
              </button>
              
              <button
                onClick={() => updateSettings({ model: ModelType.NULAR_PRO })}
                className={`p-4 text-left border transition-all rounded-none ${
                  settings.model === ModelType.NULAR_PRO
                    ? 'bg-nular-border/50 border-nular-silver text-nular-accent' 
                    : 'bg-transparent border-nular-border text-nular-dim hover:border-nular-dim'
                }`}
              >
                <div className="font-mono font-bold text-sm flex items-center gap-2">
                  Nular Pro (Llama 3 8B)
                  <span className="text-[10px] bg-indigo-900/50 text-indigo-300 px-2 py-0.5 border border-indigo-500/30 uppercase">Coding</span>
                </div>
                <div className="text-xs opacity-60 mt-1">Expert coding and complex tasks. Hosted on HF.</div>
              </button>

              <button
                onClick={() => updateSettings({ model: ModelType.NULAR_O1 })}
                className={`p-4 text-left border transition-all rounded-none ${
                  settings.model === ModelType.NULAR_O1
                    ? 'bg-nular-border/50 border-nular-silver text-nular-accent' 
                    : 'bg-transparent border-nular-border text-nular-dim hover:border-nular-dim'
                }`}
              >
                <div className="font-mono font-bold text-sm flex items-center gap-2">
                  Nular O1 (Llama 3 8B)
                  <span className="text-[10px] bg-emerald-900/50 text-emerald-300 px-2 py-0.5 border border-emerald-500/30 uppercase">Reasoning</span>
                </div>
                <div className="text-xs opacity-60 mt-1">Simulated chain-of-thought. Hosted on HF.</div>
              </button>
            </div>
          </div>

          {/* Memory */}
          <div className="space-y-4">
             <label className="text-xs uppercase tracking-wider text-nular-dim font-bold flex items-center gap-2">
              <Brain size={14} /> Memory
            </label>
            <div className="space-y-2">
               <p className="text-xs text-nular-dim">Nular learns about you as you chat. You can also edit this manually.</p>
               <textarea 
                  value={settings.userMemory || ''}
                  onChange={(e) => updateSettings({ userMemory: e.target.value })}
                  placeholder="Nular remembers..."
                  className="w-full bg-black border border-nular-border p-3 text-sm text-nular-silver h-32 focus:outline-none focus:border-nular-dim resize-none rounded-none font-mono leading-relaxed"
               />
            </div>
          </div>

          {/* Languages */}
          <div className="space-y-4">
             <label className="text-xs uppercase tracking-wider text-nular-dim font-bold flex items-center gap-2">
              <Globe size={14} /> {t.languages}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {Object.values(AppLanguage).map((lang) => (
                <button
                  key={lang}
                  onClick={() => updateSettings({ language: lang })}
                  className={`px-3 py-2 text-sm font-mono border transition-all rounded-none ${
                    settings.language === lang
                      ? 'bg-nular-border border-nular-silver text-nular-accent'
                      : 'bg-transparent border-nular-border text-nular-dim hover:text-nular-silver'
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;